import type { ExpressionStudy } from "./expression-studies";
import type { Reading } from "./types";

const WORD_PATTERN = /\p{L}+(?:[’'-]\p{L}+)*/gu;
const EXPRESSION_STANCE_IDS = ["direct", "tactful", "warm-authority", "restrained", "punchy"] as const;
const EXPRESSION_SIZE_COUNTS = { quick: 4, standard: 5, deep: 3 } as const;

export function normalizeContentWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

export function validateReading(reading: Reading) {
  const errors: string[] = [];
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(reading.id)) errors.push("id must be a stable slug");
  if (!/^\d{4}-\d{2}-\d{2}\.\d+$/.test(reading.edition)) errors.push("edition must be date.sequence");
  if (reading.taxonomyVersion !== "taxonomy_v1") errors.push("taxonomy version is unsupported");
  if (reading.provenance !== "Verbalore original") errors.push("provenance must be explicit");
  if (reading.segments.length < 8) errors.push("reading needs at least eight maintained segments");
  if (JSON.stringify(reading).includes("—")) errors.push("em dashes are not allowed in authored reading content");

  const segmentIds = new Set<string>();
  const corpus = reading.segments.map((segment) => {
    if (segmentIds.has(segment.id)) errors.push(`duplicate segment id ${segment.id}`);
    segmentIds.add(segment.id);
    return segment.text;
  }).join(" ");
  const corpusWords = new Set((corpus.match(WORD_PATTERN) || []).map(normalizeContentWord));

  for (const [key, entry] of Object.entries(reading.words)) {
    if (normalizeContentWord(entry.word) !== key) errors.push(`${key}: key and word disagree`);
    if (!corpusWords.has(key)) errors.push(`${key}: word does not occur in reading`);
    if (!corpus.includes(entry.sentence)) errors.push(`${key}: source sentence is not exact`);
    if (!entry.translations.English || !entry.translations.Hindi || !entry.translations.Punjabi) {
      errors.push(`${key}: all maintained translations are required`);
    }
  }

  for (const word of reading.exercise.targetWords) {
    if (!reading.words[normalizeContentWord(word)]) errors.push(`exercise target ${word} has no lexical entry`);
  }
  if (!reading.taxonomy.outcomes.length || !reading.taxonomy.rhetoricalMoves.length) {
    errors.push("taxonomy needs outcomes and rhetorical moves");
  }
  return errors;
}

export function assertValidCatalog(readings: Reading[]) {
  const ids = new Set<string>();
  for (const reading of readings) {
    if (ids.has(reading.id)) throw new Error(`Duplicate reading id: ${reading.id}`);
    ids.add(reading.id);
    const errors = validateReading(reading);
    if (errors.length) throw new Error(`${reading.id}: ${errors.join("; ")}`);
  }
  return readings;
}

function hasText(value: string) {
  return value.trim().length > 0;
}

function expressionText(study: ExpressionStudy) {
  return [
    study.title,
    ...study.craft,
    study.context,
    study.skill,
    study.situation,
    study.tangledDraft,
    study.intention,
    study.invariant,
    study.concept,
    study.practiceCue,
    ...study.stances.flatMap((stance) => [
      stance.label,
      stance.line,
      stance.landing,
      stance.protects,
      stance.tradeoff,
    ]),
  ].join(" ");
}

export function assertValidExpressionStudies(studies: ExpressionStudy[]) {
  const errors: string[] = [];
  const ids = new Set<string>();
  const numbers = new Set<string>();
  const sizeCounts: Record<ExpressionStudy["size"], number> = { quick: 0, standard: 0, deep: 0 };

  if (studies.length !== 12) errors.push(`expected 12 studies, received ${studies.length}`);

  for (const study of studies) {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(study.id)) errors.push(`${study.id}: id must be a stable slug`);
    if (ids.has(study.id)) errors.push(`${study.id}: duplicate id`);
    if (numbers.has(study.number)) errors.push(`${study.id}: duplicate study number ${study.number}`);
    ids.add(study.id);
    numbers.add(study.number);
    sizeCounts[study.size] += 1;

    if (study.provenance !== "Original Verbalore") errors.push(`${study.id}: provenance must be Original Verbalore`);
    if (!/^\d{2}$/.test(study.number)) errors.push(`${study.id}: number must use two digits`);
    if (study.craft.length < 2 || study.craft.some((term) => !hasText(term))) errors.push(`${study.id}: craft terms are incomplete`);
    if (![
      study.title,
      study.context,
      study.skill,
      study.situation,
      study.tangledDraft,
      study.intention,
      study.invariant,
      study.concept,
      study.practiceCue,
    ].every(hasText)) errors.push(`${study.id}: authored study fields are incomplete`);
    if (study.minutes < 1 || study.minutes > 15) errors.push(`${study.id}: minutes must remain within the MVP study range`);
    if (study.stances.length !== EXPRESSION_STANCE_IDS.length) errors.push(`${study.id}: expected five stances`);

    const stanceIds = study.stances.map((stance) => stance.id);
    if (new Set(stanceIds).size !== stanceIds.length) errors.push(`${study.id}: stance ids must be unique`);
    for (const stanceId of EXPRESSION_STANCE_IDS) {
      if (!stanceIds.includes(stanceId)) errors.push(`${study.id}: missing ${stanceId} stance`);
    }
    for (const stance of study.stances) {
      if (![stance.label, stance.line, stance.landing, stance.protects, stance.tradeoff].every(hasText)) {
        errors.push(`${study.id}/${stance.id}: articulation is incomplete`);
      }
    }

    if (expressionText(study).includes("—")) errors.push(`${study.id}: em dashes are not allowed in authored expression content`);
  }

  for (const [size, expected] of Object.entries(EXPRESSION_SIZE_COUNTS)) {
    const actual = sizeCounts[size as ExpressionStudy["size"]];
    if (actual !== expected) errors.push(`expected ${expected} ${size} studies, received ${actual}`);
  }

  if (errors.length) throw new Error(`Expression Studio content invalid: ${errors.join("; ")}`);
  return studies;
}
